(ns practitest.frontend.fields-mapping
  (:require
    [ajax.core :as ajax]
    [practitest.frontend.common.dom :refer [event-value]]
    [practitest.frontend.common.dom :as dom]
    [practitest.frontend.common.logger :as log]
    [practitest.frontend.components2.buttons :as buttons]
    [practitest.frontend.common.mixpanel :as mixpanel]
    [practitest.frontend.common.utils :as utils]
    [practitest.frontend.components.icons :as icons]
    [practitest.frontend.components.tooltip :as tooltip]
    [practitest.frontend.init :as init]
    [re-frame.core :as re-frame]))

;; interceptors
(def common-interceptors
  [re-frame/trim-v])

;; subs
(re-frame/reg-sub
  ::state
  (fn [db _]
    (:fields-mapping-state db)))

(re-frame/reg-sub
  ::options
  (fn [db _]
    (:options db)))

(re-frame/reg-sub
  ::labels
  (fn [db _]
    (get-in db [:options :labels])))

(re-frame/reg-sub
  ::errors
  (fn [db [_ invalid-key]]
    (get-in db [:options :errors invalid-key])))

(re-frame/reg-sub
  ::bugtracker-name
  :<- [::options]
  (fn [{:keys [bugtracker-name]} _]
    bugtracker-name))

(defn add-placeholder [fields-arr]
  (into [["select-placeholder" "" "select-placeholder"]] fields-arr))

(re-frame/reg-sub
  ::pt-fields
  :<- [::options]
  (fn [{:keys [pt-fields]} _]
    (add-placeholder pt-fields)))

(re-frame/reg-sub
  ::ex-fields
  :<- [::options]
  (fn [{:keys [ex-fields]} _]
    (add-placeholder ex-fields)))

(re-frame/reg-sub
  ::changes-made?
  (fn [db _]
    (:changes-made? (:fields-mapping-state db))))

(re-frame/reg-event-db
  ::mark-changes-made
  (fn [db _]
    (assoc-in db [:fields-mapping-state :changes-made?] true)))

(re-frame/reg-event-db
  ::unset-changes-made
  (fn [db _]
    (assoc-in db [:fields-mapping-state :changes-made?] false)))

(re-frame/reg-event-db
  ::cancel
  (fn [db _]
    (update db :fields-mapping-state assoc
            :message nil)
    (re-frame/dispatch [::unset-changes-made]) ;; Reset changes on cancel
    db))

(re-frame/reg-sub
  ::message
  :<- [::state]
  (fn [{:keys [message]} _]
    message))

(re-frame/reg-sub
  ::invalid?
  :<- [::state]
  (fn [{:keys [invalid?]} _]
    invalid?))

(re-frame/reg-sub
  ::fields-mapping-data
  :<- [::state]
  (fn [{:keys [fields-mapping-data]} _]
    (mapcat
      (fn [field]
        [field])
      fields-mapping-data)))

(re-frame/reg-sub
  ::valid-mappings-pairs
  :<- [::options]
  (fn [{:keys [valid-mappings-pairs]} _]
    (mapcat
      (fn [field]
        [field])
      valid-mappings-pairs)))

;; events
(re-frame/reg-event-db
  ::initialize-db
  (fn [db [_ options]]
    (let [db (if (empty? db)
               {:progress #{}}
               db)]
      (assoc db
        :options (merge (:options db) options)
        :fields-mapping-state {:bugtracker-name     (:bugtracker-name options)
                               :fields-mapping-data (:fields-mapping-data options)
                               :changes-made?       false}))))

(defn filter-by-first-value [lists key]
  (filter (fn [x] (= (first x) key)) lists))

(defn filter-by-last-value [lists key]
  (filter (fn [x] (= (last x) key)) lists))

(re-frame/reg-sub
  ::valid-field-type
  :<- [::state]
  (fn [_ [_ pt-field-type ex-field-type]]
    (let [valid-pairs @(re-frame/subscribe [::valid-mappings-pairs])
          pair (filter-by-last-value valid-pairs ex-field-type)
          valid-pt-types (map first (into [] pair))]
      (or
        (= pt-field-type "select-placeholder")
        (some #(= pt-field-type %) (flatten valid-pt-types))))))

(defn remove-row-by-index
  [fields-mapping i]
  (vec (concat (subvec fields-mapping 0 i)
               (subvec fields-mapping (inc i)))))

(re-frame/reg-event-db
  ::remove-table-row
  (fn [db [_ idx]]
    (let [fields-mapping (get-in db [:fields-mapping-state :fields-mapping-data])
          new-mapping (remove-row-by-index fields-mapping idx)]
      (re-frame/dispatch [::mark-changes-made]) ;; Mark changes when a row is removed
      (assoc-in db [:fields-mapping-state :fields-mapping-data] new-mapping))))

(re-frame/reg-event-db
  ::add-table-row
  (fn [db [_]]
    (re-frame/dispatch [::mark-changes-made]) ;; Mark changes when a row is added
    (update-in db [:fields-mapping-state :fields-mapping-data] conj {:pt_id            ""
                                                                     :ex_id            ""
                                                                     :ex_name          ""
                                                                     :map_to           "none"
                                                                     :for_issues       false
                                                                     :for_requirements false
                                                                     :fail_and_issue   false})))

(re-frame/reg-event-db
  ::bad-http-result
  [common-interceptors]
  (fn [db [error]]
    (log/info ::bad-http-result error)
    (-> db
        (assoc :error (:response error)))))

(defn update-mapping [{:keys [db]} _]
  (let [payload {:mapped-fields  (get-in db [:fields-mapping-state :fields-mapping-data])
                 :integration-id (get-in db [:options :integration-id])}]
    {:http-xhrio {:method          :post
                  :uri             (get-in db [:options :update-mapping-url])
                  :params          payload
                  :format          (ajax/json-request-format)
                  :response-format (ajax/json-response-format {:keywords? true})
                  :on-success      [::update-mapping-completed ::update-mixpanel-event]
                  :on-failure      [::bad-http-result ::update-mapping]}}))

(re-frame/reg-event-fx ::update-mapping update-mapping)

(defn update-mapping-completed [_ [_ _data]]
  (log/info ::update-mapping-completed "Updated successfully")
  (re-frame/dispatch [::unset-changes-made])) ;; Reset changes after successful update

(re-frame/reg-event-fx ::update-mapping-completed update-mapping-completed)
(re-frame/reg-event-fx ::update-mixpanel-event #(mixpanel/track "Edit ClickUp fields mapping" {"Location" "Page"}))

(re-frame/reg-event-db
  ::set-value
  (fn [db [_ field value idx]]
    (re-frame/dispatch [::mark-changes-made]) ;; Mark changes on value change
    (assoc-in db [:fields-mapping-state :fields-mapping-data idx field] value)))

(defn invalid-check [obj]
  (cond
    (= (:ex_id obj) "select-placeholder") :empty-ex-select-field
    (= (:pt_id obj) "select-placeholder") :empty-pt-select-field
    (and (= (:map_to obj) "field")
         (= (:pt_id obj) "")) :empty-pt-select-field
    (and (= (:for_issues obj) false)
         (= (:for_requirements obj) false)) :no-selected-entity
    :else nil))

(re-frame/reg-event-fx
  ::validate-mapping
  (fn [{:keys [db]} [_]]
    (let [fields-mapping-data @(re-frame/subscribe [::fields-mapping-data])
          invalid-key (some invalid-check fields-mapping-data)
          updated-successfully (get-in db [:options :errors :updated-successfully])
          err @(re-frame/subscribe [::errors invalid-key])]
      {:db (if invalid-key
             (-> db
                 (assoc-in [:fields-mapping-state :message] err)
                 (assoc-in [:fields-mapping-state :invalid?] true))
             (-> db
                 (assoc-in [:fields-mapping-state :message] updated-successfully)
                 (assoc-in [:fields-mapping-state :invalid?] false)))
       :fx (if invalid-key
             []
             [[:dispatch [::update-mapping]]])})))

;; views
(defn get-ex-field-selected-type [state idx]
  (let [ex-field-id-selected (get-in state [:fields-mapping-data idx :ex_id])
        ex-fields @(re-frame/subscribe [::ex-fields])
        ex-field-selected (filter-by-first-value ex-fields ex-field-id-selected)]
    (last (last ex-field-selected))))

(defn field-already-selected? [id key]
  (let [fields-mapping-data @(re-frame/subscribe [::fields-mapping-data])]
    (if (= id "select-placeholder")
      false
      (or
        (and (= key :pt_id)
             (some #(= id (:pt_id %)) fields-mapping-data))
        (and (= key :ex_id)
             (some #(= id (:ex_id %)) fields-mapping-data))))))

(defn select-field-ui [field-arr key idx]
  (let [state @(re-frame/subscribe [::state])
        disabled? (not (= (get-in state [:fields-mapping-data idx :map_to]) "field"))
        ex-field-selected-type (get-ex-field-selected-type state idx)]
    [:select.form-select.form-select-sm
     {:style     {:width "fit-content"
                  :margin "0 5px"}
      :on-change (fn [e]
                   (let [selected-index (.-selectedIndex (.-target e))
                         selected-ex-name (second (nth field-arr selected-index))]
                     (re-frame/dispatch [::set-value key (event-value e) idx])
                     (when (= key :ex_id)
                       (re-frame/dispatch [::set-value :ex_name selected-ex-name idx]))))
      :value     (get-in state [:fields-mapping-data idx key])
      :disabled  (if (= key :pt_id) disabled? false)}
     (doall
       (for [[id label type] field-arr]
         (when
           (or
             (= key :ex_id)
             (and
               (= key :pt_id)
               @(re-frame/subscribe [::valid-field-type type ex-field-selected-type])))
           ^{:key id} [:option
                       {:value    id
                        :disabled (field-already-selected? id key)}
                       label])))]))

(defn checkbox-field-ui [key idx]
  (let [state @(re-frame/subscribe [::state])]
    [:input.form-check-input
     {:type      "checkbox"
      :checked   (get-in state [:fields-mapping-data idx key])
      :on-change #(re-frame/dispatch [::set-value key (event-value %) idx])}]))

(defn radio-buttons-ui [label name value idx]
  (let [state @(re-frame/subscribe [::state])]
    [:div.radio.pe-2
     [:label
      [:input
       {:type      :radio
        :name      name
        :value     value
        :checked   (= (keyword (get-in state [:fields-mapping-data idx :map_to])) value)
        :on-change #(re-frame/dispatch [::set-value :map_to (event-value %) idx])}]
      (str " " label)]]))

(defn table-headers-ui []
  (let [labels @(re-frame/subscribe [::labels])]
    [:tr.table-body
     [:th (str @(re-frame/subscribe [::bugtracker-name])
               (:field-name labels))]
     [:th (:add-to-fail-and-Issue labels)]
     [:th (:map-to labels)]
     [:th (:sync-in-issues labels)]
     [:th (:sync-in-requirements labels)]
     [:th ""]
     [:th ""]]))

(defn map-to-radio-btns-select-ui [idx]
  (let [labels @(re-frame/subscribe [::labels])
        none-lbl (:none labels)
        desc-lbl (:description labels)
        fld-lbl (:field labels)]
    [:<>
     [:div.button-group
      {:style {:display         "flex"
               :justify-content "left"
               :align-items     "center"}}
      (radio-buttons-ui none-lbl (keyword "map-to-" idx) :none idx)
      (radio-buttons-ui desc-lbl (keyword "map-to-" idx) :description idx)
      [:div.col-4.d-flex.justify-content-between.align-items-center
       [:div.col-3
        {:style {:min-width "60px"}}
        (radio-buttons-ui fld-lbl (keyword "map-to-" idx) :field idx)]
       [:div.col-9 [select-field-ui @(re-frame/subscribe [::pt-fields]) :pt_id idx]]]]]))

(defn info-tooltip-ui []
  (let [label (:add-field-mapping @(re-frame/subscribe [::labels]))]
    [tooltip/hover-tooltip label
     [:span.clickable.me-1
      {:on-click #()}
      [icons/info-circle-fill]]]))

(defn remove-row-ui [idx]
  (let [label (:remove-this-row @(re-frame/subscribe [::labels]))]
    [:button.btn
     {:on-click #(re-frame/dispatch [::remove-table-row idx])
      :title    label
      :disabled false}
     [icons/svg-trash] ""]))

(defn add-row-btn-ui []
  (let [label (:add-new-mapping @(re-frame/subscribe [::labels]))]
    [:button.btn.btn-sm.btn-primary.ms-3
     {:on-click #(re-frame/dispatch [::add-table-row])
      :title    ""
      :disabled false}
     [icons/svg-plus-lg] label]))

(defn update-btn-ui []
  (let [label (:update-integration @(re-frame/subscribe [::labels]))
        changes-made? @(re-frame/subscribe [::changes-made?])]
    [:button.btn
     {:on-click (fn [_]
                  (re-frame/dispatch [::validate-mapping]))
      :class    (if changes-made? "btn-primary" "btn-secondary")
      :title    ""
      :disabled false}
     label]))

(defn cancel-btn-ui []
  (let [label (:cancel @(re-frame/subscribe [::labels]))
        cancel-url (:cancel-url @(re-frame/subscribe [::options]))]
    [:button.btn.btn-link
     {:on-click (fn [_]
                  (re-frame/dispatch [::cancel])
                  (dom/navigate! cancel-url))
      :title    ""
      :disabled false}
     label]))

(defn message-display-ui []
  (let [message @(re-frame/subscribe [::message])
        invalid? @(re-frame/subscribe [::invalid?])]
    (when (some? message)
      [:div.card-footer.text-center
       {:style {:margin-bottom "0px"
                :margin-left   "16px"}
        :class (if invalid?
                 "alert-danger"
                 "alert-info")}
       [:div.float-start.pt-1
        message]])))

(defn table-control-btns []
  [:div.pt-3.d-flex.justify-content-between.align-items-center
   [:div [add-row-btn-ui]]
   [message-display-ui]
   [:div.d-flex
    [update-btn-ui]
    [cancel-btn-ui]]])

(defn table-row [idx]
  [:tr.table-body
   [:td [select-field-ui @(re-frame/subscribe [::ex-fields]) :ex_id idx]]
   [:td [checkbox-field-ui :fail_and_issue idx]]
   [:td [map-to-radio-btns-select-ui idx]]
   [:td [checkbox-field-ui :for_issues idx]]
   [:td [checkbox-field-ui :for_requirements idx]]
   [:td [remove-row-ui idx]]
   [:td [info-tooltip-ui]]])

(defn mapping-table [state]
  (fn [state]
    (let [mapped-rows (:fields-mapping-data state)]
      [:div.col-sm-12.col-md-12.col-lg-12
       [:div.table-responsive
        [:table.table.table-condensed
         [:thead
          [table-headers-ui]]
         [:tbody
          (doall (map-indexed (fn [idx _row]
                                ^{:key idx}
                                [table-row idx])
                              mapped-rows))]]]])))

(defn fields-mapping-tab-ui [_]
  (let [state @(re-frame/subscribe [::state])]
    [:div.col
     [:div.card {:style {:width  "90%"
                         :border "none"}}
      [:div.card-body
       [:div [mapping-table state]]
       [table-control-btns]]]]))

(defn ^:dev/after-load mount-root []
  (init/remount-roots "fields-mapping-tabs-component"
                      fields-mapping-tab-ui))

(defn ^:export init [webcomponent options]
  (init/init! {:options       options
               :init-event-fn (fn [config]
                                [::initialize-db config])
               :webcomponent  webcomponent
               :root-view     fields-mapping-tab-ui}))

(utils/register-webcomponent! "fields-mapping-tabs-component")
